import { getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { eventBus } from './eventBus.js';

/**
 * Subscribe a component to one or more eventBus topics, but only actually run the
 * handler when the component's root element is attached to the live DOM and laid out.
 *
 * Why this exists: the widgets dashboard (`vue-widgets-zone`) is a `persistent: true`
 * zone — `zoneMount.unmountAll()` never unmounts it, so when the user navigates to
 * another legacy view (e.g. the agenda) the WidgetLayout Vue app stays MOUNTED on a
 * now-detached DOM element and every child KPI widget keeps its `appointments-updated`
 * subscription alive. Without this gate, closing a visit while on the agenda makes
 * every KPI widget recompute the whole park for a screen nobody is looking at — a pure
 * RAM spike (iOS jetsam, #23222).
 *
 * When the event fires while the component is detached/hidden, the run is deferred and
 * executed once the persistent zone is re-attached (`vue-persistent-zone-replaced`,
 * emitted by zoneMount when it swaps the cached element back into the new DOM position).
 * A freshly mounted (non-persistent) zone runs its own init in `onMounted`, so nothing
 * is lost there.
 *
 * @param {string|string[]} topics - eventBus topic(s) to listen to (e.g. 'appointments-updated')
 * @param {Function} handler - the work to run when visible (e.g. getData / init)
 */
export function useVisibleRefresh(topics, handler) {
	const topicList = Array.isArray(topics) ? topics : [topics];
	const instance = getCurrentInstance();
	let pending = false;

	const resolveEl = () => {
		const el = instance?.proxy?.$el;
		if (!el) return null;
		// $el is a comment/text anchor for fragment roots — fall back to its parent.
		return el.nodeType === 1 ? el : el.parentElement;
	};

	const isVisible = () => {
		const el = resolveEl();
		// isConnected rules out a detached persistent zone; getClientRects rules out display:none.
		return !!el && el.isConnected && el.getClientRects().length > 0;
	};

	const onEvent = (payload) => {
		if (isVisible()) handler(payload);
		else pending = true;
	};

	const onReattached = () => {
		if (pending && isVisible()) {
			pending = false;
			handler();
		}
	};

	onMounted(() => {
		topicList.forEach((topic) => eventBus.on(topic, onEvent));
		eventBus.on('vue-persistent-zone-replaced', onReattached);
	});

	onBeforeUnmount(() => {
		topicList.forEach((topic) => eventBus.off(topic, onEvent));
		eventBus.off('vue-persistent-zone-replaced', onReattached);
	});
}
